export const PAYMENT_ACCESS_BLOCKED_CODE = 'PAYMENT_ACCESS_BLOCKED';

export const PAYMENT_ACCESS_BLOCKED_TITLE = 'Zugang deaktiviert';

export const PAYMENT_ACCESS_BLOCKED_MESSAGE =
  'Der bezahlte Nutzungszeitraum dieser Wachabteilung ist abgelaufen. ' +
  'Bitte wendet euch an die zuständigen Administratoren. Diese können sich ' +
  'zur Klärung mit dem Betreiber der Feuerwehr-Kantine in Verbindung setzen.';


export const formatPaymentDate = value => {
  if (typeof value !== 'string') return '';
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';

  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return '';
  }
  return `${dayText}.${monthText}.${yearText}`;
};


const dayCountdown = days => `${days} ${days === 1 ? 'Tag' : 'Tagen'}`;


export const getPaymentNotice = status => {
  if (!status || typeof status !== 'object') return null;
  const overdue = status.status === 'overdue' ||
    (Number.isInteger(status.tage_bis_faellig) && status.tage_bis_faellig < 0);

  if (status.contract_status === 'cancel_at_period_end' && !overdue) {
    const paidUntil = formatPaymentDate(status.paid_until);
    return {
      kind: 'cancellation',
      tone: 'neutral',
      title: 'Nutzungsvertrag gekündigt',
      message:
        `Der Nutzungsvertrag ist zum ${paidUntil} gekündigt. ` +
        'Die Kantine kann bis einschließlich dieses Datums genutzt werden.',
      compact: false,
    };
  }

  const hasEffectiveAccess =
    typeof status.effective_access_blocked === 'boolean' &&
    typeof status.effective_access_reason === 'string';

  if (hasEffectiveAccess && status.effective_access_blocked) {
    return {
      kind: 'blocked',
      tone: 'danger',
      title: PAYMENT_ACCESS_BLOCKED_TITLE,
      message: PAYMENT_ACCESS_BLOCKED_MESSAGE,
      compact: false,
    };
  }

  if (
    hasEffectiveAccess &&
    status.effective_access_reason === 'first_payment' && !overdue
  ) {
    const dueDate = formatPaymentDate(status.naechste_zahlung_faellig);
    if (!dueDate) return null;
    return {
      kind: 'first-payment',
      tone: 'warning',
      title: 'Erste Zahlung erforderlich',
      message:
        `Die erste Zahlung muss bis zum ${dueDate} erfolgt sein. ` +
        'Andernfalls kann der Zugang zur Kantine anschließend deaktiviert werden.',
      compact: false,
    };
  }

  if (
    (hasEffectiveAccess && status.effective_access_reason === 'legacy_transition') ||
    status.access_reason === 'legacy_transition'
  ) {
    return {
      kind: 'legacy',
      tone: 'warning',
      title: 'Zahlung überfällig',
      message:
        'Der Betreiber hat den Zugang aus Kulanz weiter aktiviert. ' +
        'Bei Ausbleiben der Zahlung kann der Zugang gesperrt werden.',
      compact: false,
    };
  }

  if (
    hasEffectiveAccess &&
    status.effective_access_reason === 'force_allowed' &&
    Number.isInteger(status.tage_bis_faellig) &&
    status.tage_bis_faellig <= 0
  ) {
    return {
      kind: 'operator-allowed',
      tone: 'warning',
      title: 'Zahlung überfällig',
      message:
        'Der Zugang ist derzeit weiterhin freigegeben. ' +
        'Bei Ausbleiben der Zahlung kann der Zugang gesperrt werden.',
      compact: false,
    };
  }

  if (!hasEffectiveAccess && status.access_allowed === false) {
    return {
      kind: 'blocked',
      tone: 'danger',
      title: PAYMENT_ACCESS_BLOCKED_TITLE,
      message: PAYMENT_ACCESS_BLOCKED_MESSAGE,
      compact: false,
    };
  }

  if (
    status.access_allowed === true &&
    status.access_reason === 'payment_grace'
  ) {
    const blockAt = formatPaymentDate(status.block_at);
    const countdown = dayCountdown(status.days_until_block);
    return {
      kind: 'grace',
      tone: 'danger',
      title: 'Zahlung überfällig',
      message:
        'Die Zahlung für den nächsten Nutzungszeitraum ist überfällig. ' +
        `Der Zugang wird in ${countdown} am ${blockAt} deaktiviert.`,
      compact: false,
    };
  }

  if (overdue) {
    const dueDate = formatPaymentDate(status.naechste_zahlung_faellig);
    return {
      kind: 'overdue',
      tone: 'warning',
      title: 'Zahlung überfällig',
      message: `Laut Payment ist die Zahlung${dueDate ? ` seit dem ${dueDate}` : ''} überfällig. ` +
        'Bitte prüft den Zahlungsstatus im Payment-Dashboard. Eine Freigabe des Zugangs ersetzt die Zahlung nicht.',
      compact: false,
    };
  }

  const daysUntilDue = status.tage_bis_faellig;
  if (Number.isInteger(daysUntilDue) && daysUntilDue >= 1 && daysUntilDue <= 30) {
    const dueDate = formatPaymentDate(status.naechste_zahlung_faellig);
    return {
      kind: 'upcoming',
      tone: 'warning',
      title: 'Nächster Nutzungszeitraum',
      message:
        `Der aktuelle Nutzungszeitraum endet am ${dueDate}. ` +
        'Wenn die Kantine anschließend weiter genutzt werden soll, muss die ' +
        'Zahlung für den nächsten Zeitraum spätestens bis zu diesem Datum erfolgen.',
      compact: false,
    };
  }

  if (Number.isInteger(daysUntilDue) && daysUntilDue > 30) {
    const paidUntil = formatPaymentDate(status.paid_until);
    if (!paidUntil) return null;
    return {
      kind: 'paid',
      tone: 'success',
      title: `Bezahlt bis ${paidUntil}`,
      message:
        'Zum Ende dieses Nutzungszeitraums ist eine erneute Zahlung erforderlich, ' +
        'damit die Kantine anschließend weiter genutzt werden kann.',
      compact: true,
    };
  }

  return null;
};


export const getPaymentBlockedDetail = error => {
  const detail = error?.response?.data?.detail;
  if (
    detail &&
    typeof detail === 'object' &&
    detail.code === PAYMENT_ACCESS_BLOCKED_CODE
  ) {
    return detail;
  }
  return null;
};
